import { memo, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { Tooltip, Chip, Badge, Rating, Link } from '@mui/material';
import {
  SupportAgentOutlined,
  WarningAmberOutlined,
  InfoOutlined,
  PaymentsOutlined,
} from '@mui/icons-material';
import { GridRenderCellParams, GridRowParams, GridColDef } from '@mui/x-data-grid-pro';
import { InvoiceStatusItem } from '@features/tickets-invoices-status-select';
import { Ticket } from '@features/tickets/interfaces';
import { setOpenedId } from '@features/tickets/models/detail-sdui-opened-id/model';
import { sortToolbarChangedFunc } from '@features/toolbarSort';
import Tehzor from '@img/tehzor.svg';
import { DataGridTable } from '@shared/ui/data-grid-table';
import { HiddenArray } from '@shared/ui/hidden-array';
import { getStatusColor } from '../../libs';
import {
  $tableData,
  $pagination,
  $visibilityColumns,
  visibilityColumnsChanged,
  columnsWidthChanged,
  orderColumnsChanged,
  $columns,
  open,
  $pinnedColumns,
  changePinnedColumns,
  $rowCount,
  $sorting,
  paginationModelChanged,
  $isLoadingList,
  $selectRow,
} from '../../models';
import { CustomToolbar } from '../../molecules';
import { SortFilterToolbar } from './sortFilterToolbar';
import * as Styled from './styled';

type TypeModel = {
  formattedTypes: string;
  isTicketPaid: boolean;
};

export const sortFilterToolbarId = 'tickets-sort';

export const Table: FC = memo(() => {
  const { t } = useTranslation();
  const [
    isLoading,
    tableData,
    pagination,
    visibilityColumns,
    columns,
    count,
    pinnedColumns,
    sorting,
    selectRow,
  ] = useUnit([
    $isLoadingList,
    $tableData,
    $pagination,
    $visibilityColumns,
    $columns,
    $rowCount,
    $pinnedColumns,
    $sorting,
    $selectRow,
  ]);

  const numberIndex = columns.findIndex(
    (column: GridColDef) => column.field === 'number'
  );
  const typeIndex = columns.findIndex((column: GridColDef) => column.field === 'type');
  const paymentIndex = columns.findIndex(
    (column: GridColDef) => column.field === 'payment'
  );
  const slaIndex = columns.findIndex((column: GridColDef) => column.field === 'sla');
  const statusIndex = columns.findIndex(
    (column: GridColDef) => column.field === 'status'
  );
  const rateIndex = columns.findIndex((column: GridColDef) => column.field === 'rate');
  const deviceIndex = columns.findIndex(
    (column: GridColDef) => column.field === 'devices'
  );
  const unreadMessagesIndex = columns.findIndex(
    (column: GridColDef) => column.field === 'unreadMessages'
  );
  const priorityIndex = columns.findIndex(
    (column: GridColDef) => column.field === 'priority'
  );

  columns[numberIndex].renderCell = (params: GridRenderCellParams<Ticket, string>) => {
    const { value, row } = params;

    /* добавление номера заявки если есть провайдер и айди номера */
    const isProvider = row?.source?.id && row?.source?.provider;

    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;
    if (!newVersion) {
      return (
        <a href={`/tickets/${String(row.id)}`}>
          {isProvider ? `${value as string}/${row?.source?.id as string}` : value}
        </a>
      );
    }

    return (
      <Link>
        {isProvider ? `${value as string}/${row?.source?.id as string}` : value}
      </Link>
    );
  };

  columns[typeIndex].renderCell = (params: GridRenderCellParams<TypeModel, string[]>) => {
    const { value, row } = params;

    return (
      <Styled.TypeContainer>
        {row.formattedTypes.length > 0 ? (
          <Tooltip title={row.formattedTypes}>
            <div>
              {row.isTicketPaid ? (
                <PaymentsOutlined fontSize="small" color="action" />
              ) : (
                <InfoOutlined fontSize="small" color="action" />
              )}
            </div>
          </Tooltip>
        ) : null}
        <span>{value}</span>
      </Styled.TypeContainer>
    );
  };

  columns[paymentIndex].renderCell = (
    params: GridRenderCellParams<InvoiceStatusItem>
  ) => {
    const { value } = params;

    if (!value) {
      return null;
    }

    const paid = value.id === 'paid';

    return <Styled.Payment paid={paid}>{value.title}</Styled.Payment>;
  };

  columns[slaIndex].renderCell = (params: GridRenderCellParams<any, boolean>) => {
    const { value } = params;

    if (!value) {
      return '';
    }

    return (
      <Tooltip title={t('SLAExpired') ?? ''}>
        <WarningAmberOutlined color="error" fontSize="small" />
      </Tooltip>
    );
  };

  columns[statusIndex].renderCell = ({
    value,
    row: { isActiveSupport, isIntegrationTehzor },
  }: {
    value: string[];
    row: {
      isActiveSupport: boolean;
      isIntegrationTehzor: boolean;
    };
  }) => {
    const id = value[0];
    const title = value[1];

    const getChipIcon = () => {
      if (isIntegrationTehzor) {
        return (
          <Tooltip title={t('RequestForTehzorSupport')}>
            <Tehzor />
          </Tooltip>
        );
      }

      if (isActiveSupport) {
        return (
          <Tooltip title={t('RequestForTechnicalSupport')}>
            <SupportAgentOutlined />
          </Tooltip>
        );
      }

      return undefined;
    };

    return (
      <Chip icon={getChipIcon()} label={title} size="small" color={getStatusColor(id)} />
    );
  };

  columns[priorityIndex].renderCell = ({
    value,
  }: {
    value: { id: string; title: string };
  }) => {
    if (value.id === 'critical') {
      return <Chip label={value.title} size="small" color="error" />;
    }

    return <Chip label={value.title} size="small" variant="outlined" />;
  };

  columns[rateIndex].renderCell = (params: GridRenderCellParams<any, number>) => (
    <Rating max={5} value={params.value} readOnly size="small" />
  );

  columns[deviceIndex].renderCell = (params: GridRenderCellParams) => (
    <HiddenArray>{params.value}</HiddenArray>
  );

  columns[unreadMessagesIndex].renderCell = (
    params: GridRenderCellParams<any, number>
  ) => {
    const { value } = params;

    return (
      <Badge
        badgeContent={value}
        color={value && value > 0 ? 'primary' : 'default'}
        showZero
      />
    );
  };

  const handleClickRow = (row: GridRowParams) => {
    const newVersion = localStorage.getItem('ticketsNewVersion') === 'new' ? true : false;

    if (newVersion) {
      setOpenedId(row.id!);
      return;
    }

    open(Number(row.id));
  };

  return (
    <Styled.Container elevation={0}>
      <CustomToolbar />

      <SortFilterToolbar />

      <DataGridTable
        rows={tableData}
        columns={columns}
        sortingMode="server"
        rowCount={count}
        loading={isLoading}
        density="compact"
        onRowClick={handleClickRow}
        rowSelectionModel={selectRow}
        paginationModel={pagination}
        onPaginationModelChange={paginationModelChanged}
        sortModel={sorting}
        onSortModelChange={(gridSortModel) =>
          sortToolbarChangedFunc({ id: sortFilterToolbarId, gridSortModel })
        }
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={changePinnedColumns}
        columnVisibilityModel={visibilityColumns}
        onColumnVisibilityModelChange={visibilityColumnsChanged}
        onColumnWidthChange={columnsWidthChanged}
        onColumnOrderChange={orderColumnsChanged}
        pageSizeOptions={[25, 50, 100]}
      />
    </Styled.Container>
  );
});
